import {
  AIAssistantViewModelSkillSchema,
  AIAssistantViewModelSkillsSchema,
  RTDBPath,
  type AIAssistantClientSkill,
  type AIAssistantViewModelSkillsJSON,
  type ViewModelId,
} from 'balus-common';
import { RefBuilder, type DataSnapshot } from '@framework/repository';
import * as v from 'valibot';

const MAX_VIEW_MODEL_AI_ASSISTANT_SKILLS = 20;

export type ViewModelAIAssistantSkillMutationErrorCode =
  'invalidSkill' | 'skillLimit' | 'skillNotFound' | 'duplicateName' | 'conflict';

export type ViewModelAIAssistantSkillMutationResult =
  | { type: 'ok'; skills: AIAssistantClientSkill[] }
  | { type: 'error'; code: ViewModelAIAssistantSkillMutationErrorCode };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseSkills(value: unknown): AIAssistantClientSkill[] {
  if (!isRecord(value)) return [];

  const parsed = v.safeParse(AIAssistantViewModelSkillsSchema, value);
  if (parsed.success) {
    return Object.values(parsed.output);
  }

  const skills: AIAssistantClientSkill[] = [];
  for (const [skillId, skill] of Object.entries(value)) {
    const parsedSkill = v.safeParse(AIAssistantViewModelSkillSchema, skill);
    if (parsedSkill.success && parsedSkill.output.id === skillId) {
      skills.push(parsedSkill.output);
    }
  }
  return skills;
}

function toSkillsRecord(skills: AIAssistantClientSkill[]): AIAssistantViewModelSkillsJSON | null {
  if (skills.length === 0) return null;

  const record = Object.fromEntries(skills.map((skill) => [skill.id, skill]));
  if (Object.keys(record).length !== skills.length) {
    throw new Error('Duplicate AI assistant skill id');
  }

  const parsed = v.safeParse(AIAssistantViewModelSkillsSchema, record);
  if (!parsed.success) {
    throw new Error('Invalid view model AI assistant skills');
  }
  return parsed.output;
}

function skillsEqual(left: AIAssistantClientSkill, right: AIAssistantClientSkill): boolean {
  return (
    left.id === right.id &&
    left.enabled === right.enabled &&
    left.name === right.name &&
    left.description === right.description &&
    left.whenToUse === right.whenToUse &&
    left.argumentHint === right.argumentHint &&
    left.prompt === right.prompt &&
    left.triggers.length === right.triggers.length &&
    left.triggers.every((trigger, index) => trigger === right.triggers[index])
  );
}

function skillCollectionsEqual(left: AIAssistantClientSkill[], right: AIAssistantClientSkill[]): boolean {
  return (
    left.length === right.length &&
    left.every((skill) => {
      const other = right.find((item) => item.id === skill.id);
      return other !== undefined && skillsEqual(skill, other);
    })
  );
}

async function runMutation(
  viewModelId: ViewModelId,
  mutate: (
    skills: AIAssistantClientSkill[]
  ) => { skills: AIAssistantClientSkill[] } | { error: ViewModelAIAssistantSkillMutationErrorCode }
): Promise<ViewModelAIAssistantSkillMutationResult> {
  const ref = RefBuilder.ref(RTDBPath.ViewModel.aiAssistantSkillsPath(viewModelId));
  // Firebase compat transaction はローカルキャッシュを先に評価する。
  // 未取得の参照で条件不一致として abort するとサーバー値を読む前に終了するため、先に同期する。
  const initialData = (await ref.get()).val();
  let isFirstAttempt = true;
  let proposedData: AIAssistantViewModelSkillsJSON | null | undefined;
  let errorCode: ViewModelAIAssistantSkillMutationErrorCode | undefined;
  const result = await ref.transaction((currentData) => {
    errorCode = undefined;
    if (
      !isFirstAttempt &&
      proposedData !== undefined &&
      skillCollectionsEqual(parseSkills(currentData), parseSkills(proposedData))
    ) {
      return currentData;
    }
    // 初回は get() で取得した値を使い、競合時の再試行では transaction が渡す最新値を使う。
    const mutation = mutate(parseSkills(isFirstAttempt ? initialData : currentData));
    isFirstAttempt = false;
    if ('error' in mutation) {
      errorCode = mutation.error;
      return;
    }
    proposedData = toSkillsRecord(mutation.skills);
    return proposedData;
  });

  if (!result.committed) {
    return { type: 'error', code: errorCode ?? 'conflict' };
  }
  return { type: 'ok', skills: parseSkills(result.snapshot.val()) };
}

export class ViewModelAIAssistantSkillRepository {
  static async getSkills(viewModelId: ViewModelId): Promise<AIAssistantClientSkill[]> {
    const snapshot = await RefBuilder.ref(RTDBPath.ViewModel.aiAssistantSkillsPath(viewModelId)).get();
    return parseSkills(snapshot.val());
  }

  static async getEnabledSkills(viewModelId: ViewModelId): Promise<AIAssistantClientSkill[]> {
    return (await this.getSkills(viewModelId)).filter((skill) => skill.enabled);
  }

  static async setSkills(viewModelId: ViewModelId, skills: AIAssistantClientSkill[]): Promise<void> {
    await RefBuilder.ref(RTDBPath.ViewModel.aiAssistantSkillsPath(viewModelId)).set(toSkillsRecord(skills));
  }

  static async createSkill(
    viewModelId: ViewModelId,
    skill: AIAssistantClientSkill
  ): Promise<ViewModelAIAssistantSkillMutationResult> {
    const parsed = v.safeParse(AIAssistantViewModelSkillSchema, skill);
    if (!parsed.success) return { type: 'error', code: 'invalidSkill' };

    return runMutation(viewModelId, (skills) => {
      if (skills.length >= MAX_VIEW_MODEL_AI_ASSISTANT_SKILLS) return { error: 'skillLimit' };
      if (skills.some((currentSkill) => currentSkill.id === parsed.output.id)) return { error: 'conflict' };
      if (skills.some((currentSkill) => currentSkill.name === parsed.output.name)) return { error: 'duplicateName' };
      return { skills: [...skills, parsed.output] };
    });
  }

  static async updateSkill(
    viewModelId: ViewModelId,
    expectedSkill: AIAssistantClientSkill,
    skill: AIAssistantClientSkill
  ): Promise<ViewModelAIAssistantSkillMutationResult> {
    const parsedExpected = v.safeParse(AIAssistantViewModelSkillSchema, expectedSkill);
    const parsedSkill = v.safeParse(AIAssistantViewModelSkillSchema, skill);
    if (!parsedExpected.success || !parsedSkill.success || parsedExpected.output.id !== parsedSkill.output.id) {
      return { type: 'error', code: 'invalidSkill' };
    }

    return runMutation(viewModelId, (skills) => {
      const currentSkill = skills.find((item) => item.id === parsedSkill.output.id);
      if (!currentSkill) return { error: 'skillNotFound' };
      if (!skillsEqual(currentSkill, parsedExpected.output)) return { error: 'conflict' };
      if (
        parsedSkill.output.name !== parsedExpected.output.name &&
        skills.some((item) => item.id !== parsedSkill.output.id && item.name === parsedSkill.output.name)
      ) {
        return { error: 'duplicateName' };
      }
      return {
        skills: skills.map((item) => (item.id === parsedSkill.output.id ? parsedSkill.output : item)),
      };
    });
  }

  static async deleteSkill(
    viewModelId: ViewModelId,
    expectedSkill: AIAssistantClientSkill
  ): Promise<ViewModelAIAssistantSkillMutationResult> {
    const parsed = v.safeParse(AIAssistantViewModelSkillSchema, expectedSkill);
    if (!parsed.success) return { type: 'error', code: 'invalidSkill' };

    return runMutation(viewModelId, (skills) => {
      const currentSkill = skills.find((item) => item.id === parsed.output.id);
      if (!currentSkill) return { error: 'skillNotFound' };
      if (!skillsEqual(currentSkill, parsed.output)) return { error: 'conflict' };
      return { skills: skills.filter((item) => item.id !== parsed.output.id) };
    });
  }

  static async addMissingSkills(viewModelId: ViewModelId, skills: AIAssistantClientSkill[]): Promise<void> {
    if (skills.length === 0) return;

    const ref = RefBuilder.ref(RTDBPath.ViewModel.aiAssistantSkillsPath(viewModelId));
    await ref.transaction((currentData) => {
      const currentSkills = parseSkills(currentData);
      const currentSkillIds = new Set(currentSkills.map((skill) => skill.id));
      const skillsToAdd = skills
        .filter((skill) => !currentSkillIds.has(skill.id))
        .slice(0, Math.max(MAX_VIEW_MODEL_AI_ASSISTANT_SKILLS - currentSkills.length, 0));

      if (skillsToAdd.length === 0) return currentData;

      return toSkillsRecord([...currentSkills, ...skillsToAdd]);
    });
  }

  static subscribeSkills(
    viewModelId: ViewModelId,
    onValue: (skills: AIAssistantClientSkill[]) => void,
    onError?: (error: Error) => void
  ): () => void {
    const ref = RefBuilder.ref(RTDBPath.ViewModel.aiAssistantSkillsPath(viewModelId));
    const callback = (snapshot: DataSnapshot) => {
      onValue(parseSkills(snapshot.val()));
    };

    ref.on('value', callback, onError);
    return () => ref.off('value', callback);
  }
}
